import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  Target,
  ChevronLeft,
  ChevronRight,
  Check,
  Sparkles,
  Brain,
  TrendingUp,
  Globe,
  Users,
  ThumbsUp,
  ThumbsDown,
  Plus,
  Trash2
} from 'lucide-react';
import { BusinessGoals } from '../../types/companyProfile';

interface BusinessGoalsModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialData?: BusinessGoals;
  onSave: (data: BusinessGoals) => void;
}

const EXPANSION_OPTIONS = [
  'Yeni pazaryerleri',
  'Yurt dışı satış',
  'Yeni ürün kategorileri',
  'Fiziksel mağaza',
  'B2B satış',
  'Franchise',
  'E-ihracat',
  'Toptan satış'
];

const STRENGTH_SUGGESTIONS = [
  'Güçlü marka bilinirliği',
  'Rekabetçi fiyatlar',
  'Hızlı kargo',
  'Geniş ürün yelpazesi',
  'Kaliteli müşteri hizmetleri',
  'Özgün ürünler',
  'Güçlü tedarik zinciri',
  'Sadık müşteri tabanı'
];

const WEAKNESS_SUGGESTIONS = [
  'Düşük marka bilinirliği',
  'Yüksek maliyetler',
  'Yetersiz pazarlama',
  'Sınırlı ürün çeşitliliği',
  'Uzun kargo süreleri',
  'Yetersiz stok yönetimi',
  'Düşük kar marjı',
  'Zayıf online görünürlük'
];

const AI_LEARNING_MESSAGES = [
  { threshold: 1, message: 'Hedefler analiz ediliyor...', subtext: 'Büyüme stratejinizi anlıyoruz' },
  { threshold: 2, message: 'AI rekabet analizi yapıyor...', subtext: 'Rakiplerinizi değerlendiriyoruz' },
  { threshold: 3, message: 'SWOT modeli oluşturuluyor...', subtext: 'Güçlü ve zayıf yönler işleniyor' },
  { threshold: 4, message: 'Strateji önerileri hazırlanıyor...', subtext: 'AI sizin için çalışıyor' },
  { threshold: 5, message: 'Mükemmel! Profil güçleniyor...', subtext: 'Stratejik öneriler için hazırız' }
];

const STEPS = [
  { id: 'goals', title: 'Hedefler', icon: TrendingUp },
  { id: 'competitors', title: 'Rakipler', icon: Users },
  { id: 'swot', title: 'SWOT', icon: Target }
];

const BusinessGoalsModal: React.FC<BusinessGoalsModalProps> = ({
  isOpen,
  onClose,
  initialData,
  onSave
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<BusinessGoals>(initialData || {
    competitors: [],
    expansionPlans: [],
    strengths: [],
    weaknesses: []
  });
  const [filledCount, setFilledCount] = useState(0);
  const [aiMessage, setAiMessage] = useState(AI_LEARNING_MESSAGES[0]);
  const [showAiAnimation, setShowAiAnimation] = useState(false);
  const [newCompetitor, setNewCompetitor] = useState({ name: '', website: '' });

  // Calculate filled fields
  useEffect(() => {
    let count = 0;
    if (formData.yearlyGrowthTarget) count++;
    if (formData.expansionPlans && formData.expansionPlans.length > 0) count++;
    if (formData.competitors && formData.competitors.length > 0) count++;
    if (formData.strengths && formData.strengths.length > 0) count++;
    if (formData.weaknesses && formData.weaknesses.length > 0) count++;
    setFilledCount(count);

    // Update AI message
    const matchingMessage = [...AI_LEARNING_MESSAGES].reverse().find(m => count >= m.threshold);
    if (matchingMessage) {
      setAiMessage(matchingMessage);
    }

    // Show animation when field changes
    if (count > 0) {
      setShowAiAnimation(true);
      const timer = setTimeout(() => setShowAiAnimation(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [formData]);

  const handleInputChange = (field: keyof BusinessGoals, value: unknown) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleExpansionPlan = (plan: string) => {
    const current = formData.expansionPlans || [];
    if (current.includes(plan)) {
      handleInputChange('expansionPlans', current.filter(p => p !== plan));
    } else {
      handleInputChange('expansionPlans', [...current, plan]);
    }
  };

  const addCompetitor = () => {
    if (newCompetitor.name.trim()) {
      const current = formData.competitors || [];
      handleInputChange('competitors', [...current, { ...newCompetitor }]);
      setNewCompetitor({ name: '', website: '' });
    }
  };

  const removeCompetitor = (index: number) => {
    const current = formData.competitors || [];
    handleInputChange('competitors', current.filter((_, i) => i !== index));
  };

  const toggleStrength = (strength: string) => {
    const current = formData.strengths || [];
    if (current.includes(strength)) {
      handleInputChange('strengths', current.filter(s => s !== strength));
    } else {
      handleInputChange('strengths', [...current, strength]);
    }
  };

  const toggleWeakness = (weakness: string) => {
    const current = formData.weaknesses || [];
    if (current.includes(weakness)) {
      handleInputChange('weaknesses', current.filter(w => w !== weakness));
    } else {
      handleInputChange('weaknesses', [...current, weakness]);
    }
  };

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      onSave(formData);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-2xl mx-4 bg-[#12121a] rounded-2xl border border-white/10 shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="relative px-6 py-4 border-b border-white/10 bg-gradient-to-r from-orange-500/10 to-amber-500/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center">
                <Target className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">İş Hedefleri</h2>
                <p className="text-sm text-gray-400">Adım {currentStep + 1} / {STEPS.length}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center gap-2 mt-4">
            {STEPS.map((step, index) => {
              const StepIcon = step.icon;
              return (
                <div
                  key={step.id}
                  className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-lg transition-all ${
                    index === currentStep
                      ? 'bg-orange-500/20 border border-orange-500/50'
                      : index < currentStep
                        ? 'bg-orange-500/10 border border-orange-500/30'
                        : 'bg-white/5 border border-white/10'
                  }`}
                >
                  <StepIcon className={`w-4 h-4 ${
                    index === currentStep
                      ? 'text-orange-400'
                      : index < currentStep
                        ? 'text-orange-400'
                        : 'text-gray-500'
                  }`} />
                  <span className={`text-xs font-medium truncate ${
                    index === currentStep
                      ? 'text-orange-300'
                      : index < currentStep
                        ? 'text-orange-300'
                        : 'text-gray-500'
                  }`}>
                    {step.title}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* AI Learning Banner */}
        <div className={`px-6 py-3 bg-gradient-to-r from-purple-500/10 to-orange-500/10 border-b border-white/5 transition-all duration-500 ${
          showAiAnimation ? 'opacity-100' : 'opacity-70'
        }`}>
          <div className="flex items-center gap-3">
            <div className={`relative ${showAiAnimation ? 'animate-pulse' : ''}`}>
              <Brain className="w-5 h-5 text-purple-400" />
              {showAiAnimation && (
                <Sparkles className="absolute -top-1 -right-1 w-3 h-3 text-yellow-400 animate-bounce" />
              )}
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-purple-300">{aiMessage.message}</p>
              <p className="text-xs text-gray-400">{aiMessage.subtext}</p>
            </div>
            <div className="text-right">
              <span className="text-lg font-bold text-white">{filledCount}</span>
              <span className="text-sm text-gray-400"> / 5</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[400px] overflow-y-auto">
          {/* Step 1: Goals */}
          {currentStep === 0 && (
            <div className="space-y-5">
              {/* Yearly Growth Target */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
                  <TrendingUp className="w-4 h-4 text-orange-400" />
                  Yıllık Büyüme Hedefi (%)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    max="1000"
                    value={formData.yearlyGrowthTarget || ''}
                    onChange={(e) => handleInputChange('yearlyGrowthTarget', parseInt(e.target.value) || undefined)}
                    placeholder="Örn: 50"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-orange-500/50 focus:ring-1 focus:ring-orange-500/50 transition-all"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">%</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">Bu yıl hedeflediğiniz ciro artış oranı</p>
              </div>

              {/* Expansion Plans */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-3">
                  <Globe className="w-4 h-4 text-orange-400" />
                  Genişleme Planları
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {EXPANSION_OPTIONS.map((plan) => (
                    <button
                      key={plan}
                      type="button"
                      onClick={() => toggleExpansionPlan(plan)}
                      className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all text-left ${
                        formData.expansionPlans?.includes(plan)
                          ? 'bg-orange-500/20 border-2 border-orange-500 text-orange-300'
                          : 'bg-white/5 border-2 border-white/10 text-gray-400 hover:border-white/30'
                      }`}
                    >
                      {formData.expansionPlans?.includes(plan) && (
                        <Check className="w-3.5 h-3.5 inline mr-2" />
                      )}
                      {plan}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">Birden fazla seçebilirsiniz</p>
              </div>
            </div>
          )}

          {/* Step 2: Competitors */}
          {currentStep === 1 && (
            <div className="space-y-5">
              <div className="p-4 bg-orange-500/10 rounded-xl border border-orange-500/20">
                <p className="text-sm text-orange-300">
                  💡 Rakiplerinizi ekleyerek AI&apos;ın rekabet analizi yapmasını sağlayabilirsiniz. Web sitesi eklerseniz daha detaylı analiz yapılabilir.
                </p>
              </div>

              {/* Add Competitor */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
                  <Users className="w-4 h-4 text-orange-400" />
                  Rakip Ekle
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newCompetitor.name}
                    onChange={(e) => setNewCompetitor(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Firma adı"
                    className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-orange-500/50 focus:ring-1 focus:ring-orange-500/50 transition-all"
                  />
                  <input
                    type="url"
                    value={newCompetitor.website}
                    onChange={(e) => setNewCompetitor(prev => ({ ...prev, website: e.target.value }))}
                    placeholder="Website (opsiyonel)"
                    className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-orange-500/50 focus:ring-1 focus:ring-orange-500/50 transition-all"
                  />
                  <button
                    type="button"
                    onClick={addCompetitor}
                    disabled={!newCompetitor.name.trim()}
                    className="px-4 py-3 bg-orange-500/20 text-orange-400 rounded-xl hover:bg-orange-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Competitor List */}
              <div className="space-y-2">
                {formData.competitors?.map((competitor, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-white/5 rounded-xl border border-white/10"
                  >
                    <div>
                      <p className="text-white font-medium">{competitor.name}</p>
                      {competitor.website && (
                        <p className="text-xs text-gray-500">{competitor.website}</p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeCompetitor(index)}
                      className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}

                {(!formData.competitors || formData.competitors.length === 0) && (
                  <div className="text-center py-8 bg-white/5 rounded-xl border border-dashed border-white/10">
                    <Users className="w-10 h-10 text-gray-500 mx-auto mb-2" />
                    <p className="text-gray-400">Henüz rakip eklenmedi</p>
                    <p className="text-sm text-gray-500 mt-1">En az 3-5 rakip eklemenizi öneririz</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 3: SWOT */}
          {currentStep === 2 && (
            <div className="space-y-5">
              {/* Strengths */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-3">
                  <ThumbsUp className="w-4 h-4 text-green-400" />
                  Güçlü Yönleriniz
                </label>
                <div className="flex flex-wrap gap-2">
                  {STRENGTH_SUGGESTIONS.map((strength) => (
                    <button
                      key={strength}
                      type="button"
                      onClick={() => toggleStrength(strength)}
                      className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                        formData.strengths?.includes(strength)
                          ? 'bg-green-500/20 border border-green-500 text-green-300'
                          : 'bg-white/5 border border-white/10 text-gray-400 hover:border-white/30'
                      }`}
                    >
                      {formData.strengths?.includes(strength) && (
                        <Check className="w-3 h-3 inline mr-1" />
                      )}
                      {strength}
                    </button>
                  ))}
                </div>
              </div>

              {/* Weaknesses */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-3">
                  <ThumbsDown className="w-4 h-4 text-red-400" />
                  Zayıf Yönleriniz / Geliştirilmesi Gerekenler
                </label>
                <div className="flex flex-wrap gap-2">
                  {WEAKNESS_SUGGESTIONS.map((weakness) => (
                    <button
                      key={weakness}
                      type="button"
                      onClick={() => toggleWeakness(weakness)}
                      className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                        formData.weaknesses?.includes(weakness)
                          ? 'bg-red-500/20 border border-red-500 text-red-300'
                          : 'bg-white/5 border border-white/10 text-gray-400 hover:border-white/30'
                      }`}
                    >
                      {formData.weaknesses?.includes(weakness) && (
                        <Check className="w-3 h-3 inline mr-1" />
                      )}
                      {weakness}
                    </button>
                  ))}
                </div>
              </div>

              {/* Summary */}
              {((formData.strengths && formData.strengths.length > 0) ||
                (formData.weaknesses && formData.weaknesses.length > 0)) && (
                <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                  <h4 className="text-sm font-medium text-white mb-3">📊 SWOT Özeti</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-green-400 mb-1">Güçlü ({formData.strengths?.length || 0})</p>
                      <div className="flex flex-wrap gap-1">
                        {formData.strengths?.map((s) => (
                          <span key={s} className="text-xs px-2 py-0.5 bg-green-500/20 text-green-300 rounded">
                            {s}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-red-400 mb-1">Zayıf ({formData.weaknesses?.length || 0})</p>
                      <div className="flex flex-wrap gap-1">
                        {formData.weaknesses?.map((w) => (
                          <span key={w} className="text-xs px-2 py-0.5 bg-red-500/20 text-red-300 rounded">
                            {w}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/10 bg-[#0a0a12]/50 flex items-center justify-between">
          <button
            onClick={handleBack}
            disabled={currentStep === 0}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
              currentStep === 0
                ? 'text-gray-500 cursor-not-allowed'
                : 'text-gray-300 hover:bg-white/10'
            }`}
          >
            <ChevronLeft className="w-4 h-4" />
            Geri
          </button>

          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
            >
              Vazgeç
            </button>
            <button
              onClick={handleNext}
              className="flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
            >
              {currentStep === STEPS.length - 1 ? (
                <>
                  <Check className="w-4 h-4" />
                  Kaydet
                </>
              ) : (
                <>
                  İleri
                  <ChevronRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default BusinessGoalsModal;
